/**
 * Куда ведёт ссылка из сообщения.
 *
 * ── Зачем разбирать ────────────────────────────────────────────────────────
 *
 * Ссылка на канал или на человека внутри Telegram — не внешний ресурс. Раньше
 * любая ссылка уходила во внешний обработчик, и нажатие на @канал выкидывало
 * менеджера из приложения в браузер, где открывался веб-Telegram с ЧУЖИМ
 * аккаунтом — своим личным, а не рабочим. Такой переход не просто неудобен: он
 * ведёт человека мимо всей нашей проверки доступа и журнала действий.
 *
 * Поэтому ссылки Telegram открываются внутри, рабочим аккаунтом чата.
 */

/** Разобранная ссылка. */
export type ParsedLink =
  | { kind: 'peer'; username: string }
  /**
   * Ссылка Telegram, которую мы открыть не умеем: приглашение в закрытую
   * группу (t.me/+hash), сообщение в канале, бот с параметром.
   *
   * Выделена отдельно от внешней намеренно: отправлять её во внешний браузер
   * бессмысленно и вредно — там она откроется под личным аккаунтом.
   */
  | { kind: 'telegram-unsupported' }
  | { kind: 'external'; url: string };

/** Пути t.me, за которыми стоит не человек и не канал. */
const RESERVED = new Set([
  'joinchat',
  'addstickers',
  'addemoji',
  'proxy',
  'socks',
  'share',
  'iv',
  'login',
  'confirmphone',
  'setlanguage',
  'bg',
  'c',
]);

const USERNAME_RE = /^[A-Za-z][A-Za-z0-9_]{3,31}$/;

export function parseLink(raw: string): ParsedLink {
  const value = raw.trim();

  // Голое упоминание: @name прямо в тексте, без схемы.
  if (value.startsWith('@')) {
    const name = value.slice(1);
    return USERNAME_RE.test(name)
      ? { kind: 'peer', username: name }
      : { kind: 'telegram-unsupported' };
  }

  let url: URL;
  try {
    // Ссылки без схемы Telegram присылает как «t.me/name» — URL такое не
    // разбирает, а достраивать https:// к произвольной строке нельзя: так
    // «example.com» и «не ссылка вовсе» стали бы неразличимы.
    url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`);
  } catch {
    return { kind: 'telegram-unsupported' };
  }

  if (url.protocol === 'tg:') {
    const domain = url.searchParams.get('domain');
    return domain && USERNAME_RE.test(domain)
      ? { kind: 'peer', username: domain }
      : { kind: 'telegram-unsupported' };
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  if (host === 't.me' || host === 'telegram.me' || host === 'telegram.dog') {
    const [first, second] = url.pathname.replace(/^\/+/, '').split('/');

    /*
     * t.me/name/123 — ссылка на КОНКРЕТНОЕ сообщение в канале. Открываем сам
     * канал: перейти к сообщению мы не умеем, но привести в нужный чат —
     * половина дела, и это лучше, чем не сделать ничего.
     */
    void second;

    if (!first || first.startsWith('+') || RESERVED.has(first.toLowerCase())) {
      return { kind: 'telegram-unsupported' };
    }

    return USERNAME_RE.test(first)
      ? { kind: 'peer', username: first }
      : { kind: 'telegram-unsupported' };
  }

  return { kind: 'external', url: url.href };
}
